// @flow

import Big from "big.js";

import moment from "moment";

import wnumb from "wnumb";

import {
  addIndex,
  always,
  contains,
  curry,
  either,
  flip,
  fromPairs,
  includes,
  reject,
  take,
  all,
  anyPass,
  isNil,
  allPass,
  is,
  isEmpty,
  groupBy,
  drop,
  equals,
  filter as filterRamda,
} from "ramda";

import { Maybe } from "ramda-fantasy";

import { ID_OPCAO_PADRAO_CASAS_DECIMAIS } from "../model";

export const includesIn = flip(includes);

export const zeroToString = (valor: any) => {
  return valor === 0 ? "0" : valor;
};

export const parseQueryString = (queryString: string): { [string]: string } => {
  const params = queryString.startsWith("?")
    ? queryString.slice(1)
    : queryString;
  return fromPairs(params.split("&").map(param => param.split("=")));
};

export const formatNumber = curry(
  (
    { mark = ",", thousand = ".", decimals = 0, prefix = "", suffix = "" },
    number
  ) => {
    if (isNaN(parseFloat(number))) {
      return null;
    } else if (decimals && decimals > 7) {
      number = new Big(number).round(decimals).toNumber();
      const [a, b = ""] = new Big(number).toFixed().split(".");
      const integerPart = wnumb({
        mark,
        thousand,
        prefix,
        decimals: 0,
      }).to(parseInt(a, 10));
      const integerPartFinal = fixLostSignWnumb(a, integerPart, prefix);
      return (
        integerPartFinal +
        mark +
        take(decimals, b.padEnd(decimals, "0")) +
        suffix
      );
    } else if (decimals === ID_OPCAO_PADRAO_CASAS_DECIMAIS) {
      number = new Big(number).round(decimals).toNumber();
      const [a, b = "0"] = String(number).split(".");
      const integerPart = wnumb({
        mark,
        thousand,
        prefix,
        decimals: 0,
      }).to(parseInt(a, 10));
      const integerPartFinal = fixLostSignWnumb(a, integerPart, prefix);
      return integerPartFinal + mark + b + suffix;
    } else {
      return wnumb({ mark, thousand, decimals, prefix, suffix }).to(
        parseFloat(number)
      );
    }
  }
);

// Essa lib wnumb quando recebe o valor -0, retorna 0. Dessa forma caso o valor for -0.2918 por exemplo,
// ele viraria 0.2918 perdendo o sinal. Essa função serve como um workaround desse problema.
const fixLostSignWnumb = (integerPartRaw, integerPartParsed, prefix) => {
  const integerPartWithoutPrefix = integerPartParsed.replace(prefix, "");
  return integerPartRaw.includes("-") && !integerPartWithoutPrefix.includes("-")
    ? prefix + "-" + integerPartWithoutPrefix
    : integerPartParsed;
};

export const formatCNPJ: string => string = cnpj =>
  cnpj
    .trim()
    .replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");

export const maybeParseFloat = (value: string | number): Maybe<number> => {
  const number = parseFloat(value);
  return isNaN(number) ? Maybe.Nothing() : Maybe.Just(number);
};

// Essa função foi subscrita por outra de mesmo nome no diretório acima (../), portanto não tem funcionalidade a não ser que seja importada diretamente deste arquivo
export const maybeEmpty: any = value =>
  value != null && String(value).trim() !== ""
    ? Maybe.Just(value)
    : Maybe.Nothing();

const parseDateWithMoment = (date, format) => {
  const parsedDate = moment(date, format);
  return parsedDate.isValid()
    ? Maybe.Just(parsedDate.toDate())
    : Maybe.Nothing();
};

export const parseDateFromJSString = (date: string): Maybe<Date> => {
  const parsedFromJSString = new Date(date);
  try {
    // Força validar a data
    parsedFromJSString.toString();
    return Maybe(parsedFromJSString);
  } catch (err) {
    return Maybe.Nothing();
  }
};

export const parseDate = (
  date: string | Date | moment,
  format?: string
): Maybe<Date> => {
  if (date instanceof Date) {
    return Maybe.Just(date);
  } else if (date instanceof moment) {
    return date.isValid() ? Maybe.Just(date.toDate()) : Maybe.Nothing();
  } else if (typeof date === "string" && format != null) {
    return parseDateWithMoment(date, format);
  } else if (date.match(/\d{2}\/\d{2}\/\d{4}/)) {
    return parseDateWithMoment(date, "DD/MM/YYYY");
  } else if (date.match(/\d{2}\/\d{2}\/\d{2}/)) {
    return parseDateWithMoment(date, "DD/MM/YY");
  } else if (date.match(/\d{4}\d{2}\d{2}/)) {
    return parseDateWithMoment(date, "YYYYMMDD");
  } else if (date.match(/\d{4}-\d{2}-\d{2}/)) {
    return parseDateWithMoment(date, "YYYY-MM-DD");
  } else {
    return Maybe.Nothing();
  }
};

export const formatDate = (
  date: string | Date | moment,
  format?: string = "DD/MM/YYYY"
): string => moment(date).format(format);

const rejectIndexed = addIndex(reject);
const containsIndex = curry((indexes, val, index) => contains(index, indexes));
export const omitIndexes = curry((indexes, list) =>
  rejectIndexed(containsIndex(indexes), list)
);
export const orNull = flip(either)(always(null));

export const nullIfEmpty = (
  values: Array<number>,
  fn: (list: number[]) => number
): ?number => (values !== null && values.length > 0 ? fn(values) : null);

export const isEmptyLine = all(
  anyPass([isNil, allPass([is(String), isEmpty]), equals("null")])
);

export const getAssetsWithoutResults = (
  reportRows: any,
  offsetFixedColumns: number
): any =>
  filterRamda(
    assetRows =>
      all(
        ({ values }) => isEmptyLine(drop(offsetFixedColumns, values)),
        assetRows
      ),
    groupBy(row => row.key, reportRows)
  );

export const removePad = (value: string): any => {
  if (typeof value === "string") {
    const regA = /(,\d\d)0+$/g;
    const regB = /(,\d\d\d*?[1-9])0+$/g;
    return value.replace(regA, "$1").replace(regB, "$1");
  }
  return value;
};
